/* eslint-disable react-refresh/only-export-components */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { reducedMotion } from '../motion';

/** A cancellable presentation layer. It never delays a command or retains an old form. */
export function MotionPanel({
  motionKey,
  direction = 1,
  children,
  className = '',
  ...props
}: HTMLAttributes<HTMLDivElement> & { motionKey: string; direction?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node || reducedMotion() || !node.animate) return;
    const rtl = document.documentElement.dir === 'rtl' ? -1 : 1;
    const animation = node.animate(
      [{ transform: `translateX(${direction * rtl * 18}px)` }, { transform: 'translateX(0)' }],
      { duration: 360, easing: 'cubic-bezier(.22, 1, .36, 1)' },
    );
    const stop = () => animation.cancel();
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    media.addEventListener('change', stop);
    document.addEventListener('visibilitychange', stop);
    return () => {
      stop();
      media.removeEventListener('change', stop);
      document.removeEventListener('visibilitychange', stop);
    };
  }, [motionKey, direction]);
  return (
    <div ref={ref} className={`motion-panel ${className}`} {...props}>
      {children}
    </div>
  );
}

/** Content is always visible. An observer adds a finite reveal only when it enters view. */
export function RevealRegion({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const region = ref.current;
    if (!region || reducedMotion() || !('IntersectionObserver' in window)) return;
    const animations = new Set<Animation>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer.unobserve(entry.target);
          if (document.visibilityState !== 'visible' || reducedMotion() || !entry.target.animate)
            continue;
          const animation = entry.target.animate(
            [
              { transform: 'translateY(12px)', opacity: 0.65 },
              { transform: 'translateY(0)', opacity: 1 },
            ],
            { duration: 480, easing: 'cubic-bezier(.22, 1, .36, 1)' },
          );
          animations.add(animation);
          void animation.finished
            .catch(() => undefined)
            .finally(() => animations.delete(animation));
        }
      },
      { threshold: 0.12 },
    );
    region.querySelectorAll('[data-reveal]').forEach((element) => observer.observe(element));
    const stop = () => animations.forEach((animation) => animation.cancel());
    document.addEventListener('visibilitychange', stop);
    return () => {
      observer.disconnect();
      stop();
      document.removeEventListener('visibilitychange', stop);
    };
  }, []);
  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}

/** One moving selection surface for horizontal, vertical and RTL navigation. */
export function ActiveIndicator({ activeKey }: { activeKey: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const indicator = ref.current;
    const parent = indicator?.parentElement;
    if (!indicator || !parent) return;
    let frame = 0;
    const measure = () => {
      const target = parent.querySelector<HTMLElement>(
        '[aria-current="page"], [aria-current="step"]',
      );
      if (!target) {
        indicator.style.opacity = '0';
        return;
      }
      const outer = parent.getBoundingClientRect();
      const rect = target.getBoundingClientRect();
      indicator.style.width = `${rect.width}px`;
      indicator.style.height = `${rect.height}px`;
      indicator.style.transform = `translate(${rect.left - outer.left + parent.scrollLeft}px, ${rect.top - outer.top + parent.scrollTop}px)`;
      indicator.style.opacity = '1';
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(parent);
    parent.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      observer?.disconnect();
      cancelAnimationFrame(frame);
      parent.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [activeKey]);
  return <span ref={ref} className="active-indicator" aria-hidden="true" />;
}
export const progressStyle = (index: number, total: number) =>
  ({ '--progress': `${(index / Math.max(1, total - 1)) * 100}%` }) as CSSProperties;
